import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { trackPurchase } from "@/lib/analytics";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tx, setTx] = useState(null);
  const fired = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          if (!fired.current) {
            fired.current = true;
            trackPurchase({
              session_id: sessionId,
              amount: data.amount,
              kind: data.metadata?.kind,
              label: data.metadata?.label,
            });
          }
          setStatus("paid");
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts++ < 6) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch { setStatus("error"); }
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-[60vh] flex items-center justify-center px-6 py-24">
      <div className="bg-white border border-[color:var(--color-border)] rounded-lg p-10 max-w-md w-full text-center">
        {status === "checking" && (<>
          <Loader2 className="w-12 h-12 text-[color:var(--color-primary)] animate-spin mx-auto mb-4" />
          <h2 className="font-display text-2xl">Confirming your payment…</h2>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h2 className="font-display text-3xl text-[color:var(--color-primary)] mb-2">Thank you!</h2>
          <p className="text-[color:var(--color-text-secondary)] mb-6">{tx?.metadata?.label} — £{tx?.amount?.toFixed(2)} paid.</p>
          <Link to="/" className="btn-primary justify-center" data-testid="payment-home-btn">Back to home</Link>
        </>)}
        {status === "pending" && (<>
          <Loader2 className="w-12 h-12 text-[color:var(--color-primary)] mx-auto mb-4" />
          <h2 className="font-display text-2xl mb-2">Still processing</h2>
          <p>You'll receive an email once the payment completes.</p>
        </>)}
        {(status === "expired" || status === "error") && (<>
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="font-display text-2xl mb-2">Payment incomplete</h2>
          <p className="text-[color:var(--color-text-secondary)] mb-6">Please try again.</p>
          <Link to="/" className="btn-primary justify-center">Back to home</Link>
        </>)}
      </div>
    </div>
  );
}
